import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useScrollableTabs } from '../../hooks/useScrollableTabs';
import { useTheme } from '../../contexts/ThemeContext';

const OperationsTabBar = ({
  tabs,
  activeTab,
  onChange,
  idPrefix = 'operations'
}) => {
  const { t } = useTranslation();
  const { border, text, cn } = useTheme();
  const buttonRefs = useRef([]);
  const {
    tabsRef,
    canScrollLeft,
    canScrollRight,
    checkTabsScroll,
    scrollTabs
  } = useScrollableTabs({ refreshKey: tabs.map((tab) => tab.id).join('|') });

  const activateAt = (index) => {
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab.id);
    buttonRefs.current[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    activateAt(nextIndex);
  };

  return (
    <div className="relative flex items-center gap-1">
      {canScrollLeft && (
        <button
          type="button"
          className={cn('rounded p-2', text.secondary)}
          aria-label={t('previous')}
          onClick={() => scrollTabs('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      <div
        ref={tabsRef}
        role="tablist"
        aria-label={t('operations.title')}
        className={cn(
          'flex min-w-0 flex-1 gap-1 overflow-x-auto border-b scrollbar-hide',
          border.default
        )}
        onScroll={checkTabsScroll}
      >
        {tabs.map((tab, index) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                buttonRefs.current[index] = node;
              }}
              id={`${idPrefix}-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${idPrefix}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={cn(
                'min-h-11 shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium',
                selected
                  ? 'border-amber-600 text-amber-700 dark:text-amber-300'
                  : `border-transparent ${text.secondary}`
              )}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {canScrollRight && (
        <button
          type="button"
          className={cn('rounded p-2', text.secondary)}
          aria-label={t('next')}
          onClick={() => scrollTabs('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default OperationsTabBar;
